import type { AxiosResponse } from "axios";
import type { ApiResponseMeta } from "src/api/public-types";

/* page token 방식 목록 API 한 페이지 정규화 */
export type TokenPage<TItem> = {
  items: TItem[];
  nextPageToken?: string;
};

type PageRequestError = Error & { errorCode?: string };

/*
 * 토큰이 무효해졌다고 보는 응답 코드
 * eET0516 은 BFF 공통 "Invalid Data", 나머지는 TAC22 명세
 */
const DEAD_TOKEN_ERROR_CODES = new Set(["eET0516", "page_token_not_found", "page_token_query_mismatch", "page_token_expired"]);

/*
 * useApiOperation 은 실패해도 throw 하지 않고 에러 응답(네트워크 실패면 undefined)을 반환하므로
 * 200 이 아니면 errorCode 를 담아 throw 해야 호출부 catch 에서 실패 처리 가능
 */
export const unwrapPageResponse = <TData>(response: AxiosResponse<TData> | undefined): TData => {
  if (response?.status === 200) return response.data;

  const meta = (response?.data as { meta?: ApiResponseMeta } | undefined)?.meta;
  const error: PageRequestError = new Error(meta?.errorMessage ?? "page request failed");
  error.errorCode = meta?.errorCode;
  throw error;
};

/* 같은 토큰으로 재시도해도 계속 실패하므로 첫 페이지부터 다시 받아야 하는 에러 */
export const isDeadPageTokenError = (error: unknown): boolean => {
  const errorCode = (error as PageRequestError | null)?.errorCode;
  return errorCode !== undefined && DEAD_TOKEN_ERROR_CODES.has(errorCode);
};

/*
 * 마지막 페이지까지 이어받기 (startPageToken 생략 시 첫 페이지부터)
 * 0건 페이지가 와도 토큰이 있으면 계속 받음
 */
export const fetchAllFromPageToken = async <TItem>(
  fetchPage: (pageToken?: string) => Promise<TokenPage<TItem>>,
  startPageToken?: string,
): Promise<TItem[]> => {
  const items: TItem[] = [];
  let pageToken: string | undefined = startPageToken;
  do {
    const page: TokenPage<TItem> = await fetchPage(pageToken);
    items.push(...page.items);
    pageToken = page.nextPageToken;
  } while (pageToken);
  return items;
};
